import { useState } from 'react';
import { Badge, Button, Csel, Icon, Input, Section, Switch, useToast } from '@efira/ui';

import {
  useOverlayStyle,
  useRestoreSetting,
  useSaveOverlayStyle,
  useSaveSetting,
} from '../../../api/queries';
import type { BindingsPayload, SettingField } from '../../../api/queries';
import { label } from '../../../bindings/labels';
import { Collections } from './Collections';
import { MediaPicker } from './MediaPicker';

/**
 * «Настройки игры» в левой колонке.
 *
 * СОСТАВ ЗАВИСИТ ОТ ИГРЫ, ТИПЫ ПОЛЕЙ ОБЩИЕ: перечисление, число со своими
 * границами, тумблер, условное поле, знак «?» с пояснением, выбор цвета и
 * подборщик изображения (docs/reference/LIVECADE_UX.md, 4.2).
 *
 * ПРАВИЛО 1 ВИДИМОСТИ: условное поле НЕАКТИВНО С ПРИЧИНОЙ, а не отсутствует.
 * У эталона три таких поля физически нет в разметке, пока не переключат
 * соседнее, - и пользователь не знает, что настройка существует, пока не
 * наткнётся на неё случайно.
 *
 * ГРУППА «ФОН» - НЕ ИЗ МАНИФЕСТА. Фон принадлежит кадру, а не сцене: он
 * одинаков у всех десяти игр и хранится вместе с показом легенды
 * (`apps/server/src/world/overlay-style.ts`). У эталона он стоит последней
 * группой настроек игры - у нас так же, потому что стример ищет его там.
 */
export function GameSettings({ gameId, data }: { gameId: string; data: BindingsPayload }) {
  const fields = data.manifest.settings.fields;

  /*
   * ГРУППЫ - ИЗ СХЕМЫ, А НЕ ИЗ ПОРЯДКА ПОЛЕЙ. Игра объявляет `groupKey`, и
   * поля без него идут общим списком выше групп: игре, которой делить
   * нечего, деление ничего не стоит.
   */
  const plain = fields.filter((field) => field.groupKey === undefined);
  const groups: { key: string; fields: SettingField[] }[] = [];

  for (const field of fields) {
    if (field.groupKey === undefined) continue;

    const last = groups.find((group) => group.key === field.groupKey);

    if (last === undefined) {
      groups.push({ key: field.groupKey, fields: [field] });
    } else {
      last.fields.push(field);
    }
  }

  const row = (field: SettingField) => (
    <li key={field.id}>
      <SettingRow
        gameId={gameId}
        field={field}
        fields={fields}
        values={data.settingValues}
        changed={data.settingsChanged}
        factory={data.settingsFactory}
      />
    </li>
  );

  return (
    <Section title="Настройки игры" id="game-settings">
      {fields.length === 0 ? (
        <p className="text-foreground/80 text-sm">У этой игры своих настроек нет.</p>
      ) : (
        <>
          {plain.length > 0 && <ul className="flex flex-col gap-3">{plain.map(row)}</ul>}

          {groups.map((group) => (
            <div key={group.key} data-gs-group={group.key} className="mt-4">
              <p
                data-gs-group-label={group.key}
                className="text-foreground/80 mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide uppercase"
              >
                <Icon name="settings" size="sm" />
                {label(group.key)}
              </p>
              <ul className="flex flex-col gap-3">{group.fields.map(row)}</ul>
            </div>
          ))}
        </>
      )}

      <Collections
        gameId={gameId}
        collections={data.manifest.collections}
        bindings={data.bindings}
      />

      <Background gameId={gameId} />
    </Section>
  );
}

function SettingRow({
  gameId,
  field,
  fields,
  values,
  changed,
  factory,
}: {
  gameId: string;
  field: SettingField;
  fields: SettingField[];
  values: Record<string, boolean | number | string>;
  changed: string[];
  factory: Record<string, boolean | number | string>;
}) {
  const save = useSaveSetting(gameId);
  const restore = useRestoreSetting(gameId);
  const value = values[field.id];
  const gate = field.visibleWhen;
  const blocked = gate !== undefined && values[gate.field] !== gate.equals;
  const isChanged = changed.includes(field.id);

  const gateField = gate === undefined ? undefined : fields.find((item) => item.id === gate.field);
  const reason =
    blocked && gate !== undefined
      ? `Доступно, когда «${gateField === undefined ? gate.field : label(gateField.labelKey)}» включено`
      : undefined;

  const restoreLink = isChanged ? (
    <Button
      variant="ghost"
      className="self-start"
      data-gs-restore={field.id}
      /* Не `text-primary`: на 12 px он не проходит AA в светлой теме. */
      onClick={() => {
        restore.mutate(field.id);
      }}
    >
      вернуть заводское
      {factory[field.id] === undefined ? '' : `: ${String(factory[field.id])}`}
    </Button>
  ) : null;

  if (field.type === 'editor-link') {
    const quota = field.editor?.quota;

    return (
      /* Правило 5: точка входа в редактор ВЫГЛЯДИТ точкой входа, а не строкой
         настройки. Правило 4: счётчик квоты стоит здесь, в точке решения. */
      <div
        data-gs-editor={field.id}
        className="border-primary/40 bg-primary/5 rounded-lg flex flex-col gap-1 border p-2"
      >
        <span className="text-sm font-medium">{label(field.labelKey)}</span>
        <span className="text-foreground/80 text-xs">
          Отдельный редактор
          {quota === undefined ? '' : ` · свои материалы: 0 из ${quota.limit}`}
        </span>
        <Button variant="secondary" size="s" className="mt-1 gap-1 self-start">
          <Icon name="open" size="sm" />
          Открыть редактор
        </Button>
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div data-gs-field={field.id} className="flex flex-col gap-1">
        <label className="flex items-center gap-2 text-sm">
          <Switch
            data-gs-toggle={field.id}
            checked={value === true}
            disabled={blocked}
            title={reason}
            onCheckedChange={(next) => {
              save.mutate({ id: field.id, value: next === true });
            }}
          />
          <span className="min-w-0 flex-1">{label(field.labelKey)}</span>
          <Help field={field} />
        </label>
        {reason !== undefined && <span className="text-foreground/80 text-xs">{reason}</span>}
        {restoreLink}
      </div>
    );
  }

  if (field.type === 'select') {
    const options = (field.values ?? []).map((option) => ({
      value: option,
      label: label(`${field.labelKey}.${option}`),
    }));

    return (
      <div
        data-gs-field={field.id}
        data-gs-subfield={gate === undefined ? undefined : field.id}
        className="flex flex-col gap-1"
      >
        <span className="flex items-center gap-2 text-xs">
          <span className="text-foreground/80 min-w-0 flex-1">{label(field.labelKey)}</span>
          <Help field={field} />
        </span>

        {/*
          ОДИН КОМПОНЕНТ НА ЛЮБОЙ СПИСОК. Поиск он заводит сам, когда пунктов
          много (`CSEL_SEARCH_FROM`): раньше здесь стояли ДВА разных списка -
          свой для длинных, родной `select` для коротких, - и вели они себя
          по-разному ровно там, где это заметно: в узкой колонке.
        */}
        <span data-gs-select={field.id}>
          <Csel
            value={String(value ?? '')}
            options={options}
            disabled={blocked}
            aria-label={label(field.labelKey)}
            triggerClassName="w-full"
            onChange={(next) => {
              save.mutate({ id: field.id, value: next });
            }}
          />
        </span>

        {reason !== undefined && <span className="text-foreground/80 text-xs">{reason}</span>}
        {restoreLink}
      </div>
    );
  }

  return (
    <NumberOrText
      field={field}
      value={value}
      blocked={blocked}
      reason={reason}
      restoreLink={restoreLink}
      onSave={(next) => {
        save.mutate({ id: field.id, value: next });
      }}
    />
  );
}

/**
 * Число и текст.
 *
 * СОХРАНЯЮТСЯ ПО ОКОНЧАНИИ ВВОДА, а не на каждое нажатие: иначе «30» по
 * дороге к «300» успело бы стать «3», «30», «300» - тремя запросами, из
 * которых первый применился бы к идущему раунду.
 */
function NumberOrText({
  field,
  value,
  blocked,
  reason,
  restoreLink,
  onSave,
}: {
  field: SettingField;
  value: boolean | number | string | undefined;
  blocked: boolean;
  reason: string | undefined;
  restoreLink: React.ReactNode;
  onSave: (value: number | string) => void;
}) {
  const [draft, setDraft] = useState(String(value ?? ''));
  const [editing, setEditing] = useState(false);
  const shown = editing ? draft : String(value ?? '');

  return (
    <label
      data-gs-field={field.id}
      data-gs-subfield={field.visibleWhen === undefined ? undefined : field.id}
      className="flex flex-col gap-1"
    >
      <span className="flex items-center gap-2 text-xs">
        <span className="text-foreground/80 min-w-0 flex-1">{label(field.labelKey)}</span>
        <Help field={field} />
      </span>

      <Input
        data-gs-number={field.type === 'number' ? field.id : undefined}
        data-gs-text={field.type === 'text' ? field.id : undefined}
        type={field.type === 'number' ? 'number' : 'text'}
        value={shown}
        disabled={blocked}
        title={reason}
        min={field.min}
        max={field.max}
        onFocus={() => {
          setDraft(String(value ?? ''));
          setEditing(true);
        }}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          setEditing(false);

          if (field.type === 'number') {
            const next = Number(draft);

            if (Number.isFinite(next) && next !== value) {
              onSave(next);
            }

            return;
          }

          if (draft !== value) {
            onSave(draft);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
        }}
      />

      {/* Предел объявлен ЧИСЛОМ рядом с полем - правило видимости 3. */}
      {(field.min !== undefined || field.max !== undefined) && (
        <span className="text-foreground/80 text-xs">
          допустимо от {field.min ?? 0} до {field.max ?? '∞'}
        </span>
      )}
      {reason !== undefined && <span className="text-foreground/80 text-xs">{reason}</span>}
      {restoreLink}
    </label>
  );
}

/** Знак «?» рядом с подписью: пояснение по наведению И по фокусу. */
function Help({ field }: { field: SettingField }) {
  const hint = label(`${field.labelKey}.help`);

  if (hint === `${field.labelKey}.help`) {
    return null;
  }

  return (
    <span
      data-gs-help={field.id}
      tabIndex={0}
      role="note"
      aria-label={hint}
      title={hint}
      className="border-foreground/30 text-foreground/80 grid size-4 shrink-0 place-items-center rounded-full border text-xs"
    >
      ?
    </span>
  );
}

/**
 * Группа «Фон».
 *
 * ПРОЗРАЧНЫЙ ФОН ВКЛЮЧЁН ПО УМОЛЧАНИЮ и БЛОКИРУЕТ выбор цвета - как у
 * эталона. Причина у нас названа словами, а у эталона кнопка просто серая.
 *
 * Изображение помечено Pro. Подборщик при этом открывается и на Free:
 * функция объявлена платной, но спрятать её значило бы не сказать, что она
 * есть.
 */
function Background({ gameId }: { gameId: string }) {
  const style = useOverlayStyle(gameId);
  const save = useSaveOverlayStyle(gameId);
  const toast = useToast();
  const [picker, setPicker] = useState(false);
  const [media, setMedia] = useState(false);

  if (style.data === undefined) {
    return null;
  }

  const values = style.data.values;
  const transparent = values['bgTransparent'] === true;
  /* eslint-disable-next-line no-restricted-syntax -- это ЗНАЧЕНИЕ настройки
     игры, а не цвет интерфейса: заводской фон оверлея, который уезжает в
     оверлей. У оверлея по решению проекта нет ни темы, ни токенов. */
  const color = String(values['bgColor'] ?? '#0c1512');
  const image = String(values['bgImage'] ?? '');

  return (
    <div data-gs-group="background" className="border-foreground/10 mt-4 border-t pt-3">
      <p
        data-gs-group-label="background"
        className="text-foreground/80 mb-2 flex items-center gap-2 text-xs font-semibold tracking-wide uppercase"
      >
        <Icon name="layout" size="sm" />
        Фон
      </p>

      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-2 text-sm">
          <Switch
            data-gs-transparent
            checked={transparent}
            onCheckedChange={(next) => {
              save.mutate({ id: 'bgTransparent', value: next === true });
            }}
          />
          <span className="min-w-0 flex-1">Прозрачный</span>
        </label>
        <span className="text-foreground/80 -mt-2 text-xs">
          Оверлей не красится вовсе: в OBS под ним видно камеру.
        </span>

        <div className="flex flex-col gap-1">
          <span className="text-foreground/80 text-xs">Цвет</span>
          <Button
            variant="secondary"
            size="s"
            className="justify-start gap-2"
            data-gs-color
            aria-expanded={!transparent && picker}
            disabled={transparent}
            title={transparent ? 'Доступно, когда фон не прозрачный' : undefined}
            onClick={() => {
              setPicker((value) => !value);
            }}
          >
            <span
              aria-hidden
              className="border-foreground/20 size-4 rounded border"
              style={{ backgroundColor: color }}
            />
            {color}
          </Button>
          {transparent && (
            <span className="text-foreground/80 text-xs">Доступно, когда фон не прозрачный.</span>
          )}

          {!transparent && picker && (
            <div
              data-gs-color-popover
              className="border-foreground/15 rounded-lg mt-1 flex flex-col gap-2 border p-2"
            >
              {/* eslint-disable-next-line no-restricted-syntax -- выбор цвета: у пресета компонента нет, искали color-picker в реестре shadcn - нет ни в ui, ни в блоках. Нативный ввод даёт системную палитру и пипетку */}
              <input
                type="color"
                aria-label="Цвет фона"
                data-gs-color-input
                className="h-10 w-full"
                value={color}
                onChange={(event) => {
                  save.mutate({ id: 'bgColor', value: event.target.value });
                }}
              />
              <label className="flex items-center gap-2 text-xs">
                HEX
                <Input
                  data-gs-color-hex
                  value={color}
                  onChange={(event) => {
                    const next = event.target.value;

                    if (/^#[0-9a-f]{6}$/i.test(next)) {
                      save.mutate({ id: 'bgColor', value: next });
                    }
                  }}
                  className="w-28"
                />
              </label>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-xs">
            <span className="text-foreground/80 min-w-0 flex-1">Изображение — поверх цвета</span>
            <Badge data-gs-pro variant="default">
              Pro
            </Badge>
          </span>

          <div
            data-gs-media-current
            className="border-foreground/15 rounded-lg text-foreground/80 flex h-16 items-center justify-center overflow-hidden border text-xs"
          >
            {image === '' ? (
              'Изображения нет'
            ) : (
              <img src={image} alt="" className="size-full object-cover" />
            )}
          </div>

          <Button
            variant="secondary"
            size="s"
            className="self-start"
            data-gs-media-choose
            aria-expanded={media}
            onClick={() => {
              setPicker(false);
              setMedia(true);
            }}
          >
            Выбрать
          </Button>
        </div>
      </div>

      {media && (
        <MediaPicker
          gameId={gameId}
          current={image}
          onClose={() => {
            setMedia(false);
          }}
          onPick={(url) => {
            save.mutate(
              { id: 'bgImage', value: url },
              {
                onSuccess: () => {
                  toast.show({ tone: 'success', title: 'Фоновое изображение применено' });
                },
                onError: (error) => {
                  const reason =
                    typeof error === 'object' && error !== null && 'reason' in error
                      ? String((error as { reason: unknown }).reason)
                      : 'Изображение не применилось.';
                  toast.show({ tone: 'warning', title: 'Не применилось', text: reason });
                },
              },
            );
            setMedia(false);
          }}
        />
      )}
    </div>
  );
}
